'use client';

import {
  isSidebarTranslationProcessing,
  patchSidebarTranslationHistory,
  sidebarHistoryKeys,
} from '@/components/sidebar-history/sidebar-history-cache';
import {
  getTranslateHistory,
  getTranslateInfo,
  type TranslationSession,
} from '@/app/services/translation';
import type { QueryClient, InfiniteData } from '@tanstack/react-query';
import { useEffect, useId, useMemo, useState } from 'react';

const HIGH_FREQUENCY_POLL_MS = 6_000;
const LOW_FREQUENCY_DISCOVERY_MS = 60_000;

export const translateHistoryInfiniteKey = [
  'translate-history-infinite',
] as const;
export const getTranslateDetailQueryKey = (id: string) =>
  ['translate-detail', id] as const;

type PollOwnerKind = 'preview' | 'histories' | 'sidebar';

type PollOwnerListener = (ownerId: string | null) => void;

interface PollOwnerEntry {
  id: string;
  kind: PollOwnerKind;
}

interface TranslateHistoryPage {
  data: {
    list: TranslationSession[];
    total: number;
  };
}

interface PollOwnerSnapshot {
  ownerId: string | null;
  listeners: Set<PollOwnerListener>;
  entries: Map<string, PollOwnerEntry>;
}

const ownerSnapshot: PollOwnerSnapshot = {
  ownerId: null,
  listeners: new Set(),
  entries: new Map(),
};

function getOwnerPriority(kind: PollOwnerKind) {
  if (kind === 'preview') {
    return 3;
  }
  return kind === 'histories' ? 2 : 1;
}

function recomputeOwner() {
  let nextOwnerId: string | null = null;
  let nextPriority = -1;

  ownerSnapshot.entries.forEach((entry, id) => {
    const priority = getOwnerPriority(entry.kind);
    if (priority > nextPriority) {
      nextPriority = priority;
      nextOwnerId = id;
    }
  });

  if (ownerSnapshot.ownerId === nextOwnerId) {
    return;
  }

  ownerSnapshot.ownerId = nextOwnerId;
  ownerSnapshot.listeners.forEach((listener) => listener(nextOwnerId));
}

function subscribeOwner(listener: PollOwnerListener) {
  ownerSnapshot.listeners.add(listener);
  return () => {
    ownerSnapshot.listeners.delete(listener);
  };
}

function registerOwnerEntry(entry: PollOwnerEntry) {
  ownerSnapshot.entries.set(entry.id, entry);
  recomputeOwner();
  return () => {
    ownerSnapshot.entries.delete(entry.id);
    recomputeOwner();
  };
}

function usePollOwnership(kind: PollOwnerKind, enabled: boolean) {
  const id = `${kind}-${useId()}`;
  const [ownerId, setOwnerId] = useState<string | null>(ownerSnapshot.ownerId);

  useEffect(() => {
    const unsubscribe = subscribeOwner(setOwnerId);
    return () => {
      unsubscribe();
    };
  }, []);

  useEffect(() => {
    if (!enabled) {
      return;
    }
    return registerOwnerEntry({ id, kind });
  }, [enabled, id, kind]);

  return enabled && ownerId === id;
}

function mergeLatestFirstPage(
  currentPages: TranslateHistoryPage[],
  latestFirstPage: TranslateHistoryPage,
) {
  if (currentPages.length === 0) {
    return currentPages;
  }

  const pageSizes = currentPages.map((page) => page.data.list.length);
  const firstPageIds = new Set(
    latestFirstPage.data.list.map((item) => item.file_translation_session_id),
  );

  const remainingItems = currentPages
    .flatMap((page) => page.data.list)
    .filter((item) => !firstPageIds.has(item.file_translation_session_id));

  const mergedItems = [...latestFirstPage.data.list, ...remainingItems].slice(
    0,
    latestFirstPage.data.total,
  );
  let cursor = 0;

  return currentPages.map((page, index) => {
    const pageSize =
      index === 0 ? latestFirstPage.data.list.length : pageSizes[index];
    const list = mergedItems.slice(cursor, cursor + pageSize);
    cursor += pageSize;

    if (index === 0) {
      return {
        ...page,
        data: {
          ...page.data,
          ...latestFirstPage.data,
          list,
        },
      };
    }

    return {
      ...page,
      data: {
        ...page.data,
        total: latestFirstPage.data.total,
        list,
      },
    };
  });
}

function patchInfiniteTranslateHistoryItem(
  queryClient: QueryClient,
  queryKey: readonly unknown[],
  nextItem: TranslationSession,
) {
  queryClient.setQueryData<InfiniteData<TranslateHistoryPage>>(
    queryKey,
    (prev) => {
      if (!prev) {
        return prev;
      }

      return {
        ...prev,
        pages: prev.pages.map((page) => ({
          ...page,
          data: {
            ...page.data,
            list: page.data.list.map((item) =>
              item.file_translation_session_id ===
              nextItem.file_translation_session_id
                ? { ...item, ...nextItem }
                : item,
            ),
          },
        })),
      };
    },
  );
}

function patchTranslateDetailItem(
  queryClient: QueryClient,
  nextItem: TranslationSession,
) {
  queryClient.setQueryData(
    getTranslateDetailQueryKey(nextItem.file_translation_session_id),
    nextItem,
  );
}

function replaceInfiniteTranslateHistoryFirstPage(
  queryClient: QueryClient,
  queryKey: readonly unknown[],
  latestFirstPage: TranslateHistoryPage,
) {
  queryClient.setQueryData<InfiniteData<TranslateHistoryPage>>(
    queryKey,
    (prev) => {
      if (!prev) {
        return prev;
      }

      return {
        ...prev,
        pages: mergeLatestFirstPage(prev.pages, latestFirstPage),
      };
    },
  );
}

interface UseTranslateHistoryPollingParams {
  kind: PollOwnerKind;
  queryClient: QueryClient;
  items: TranslationSession[];
  enabled?: boolean;
  discoveryEnabled?: boolean;
  pageSize?: number;
  keyword?: string;
  detailId?: string;
  infiniteQueryKey?: readonly unknown[];
}

export function useTranslateHistoryPolling({
  kind,
  queryClient,
  items,
  enabled = true,
  discoveryEnabled = true,
  pageSize = 25,
  keyword,
  detailId,
  infiniteQueryKey,
}: UseTranslateHistoryPollingParams) {
  const isOwner = usePollOwnership(kind, enabled);
  const normalizedKeyword = keyword?.trim() ?? '';

  const processingIds = useMemo(
    () =>
      items
        .filter((item) => isSidebarTranslationProcessing(item.status))
        .map((item) => item.file_translation_session_id),
    [items],
  );
  const processingIdsKey = processingIds.join('|');
  const hasProcessingItems = processingIds.length > 0;

  useEffect(() => {
    if (!isOwner) {
      return;
    }

    const syncSidebarFromInfiniteQuery = () => {
      if (normalizedKeyword || !infiniteQueryKey) {
        return;
      }

      const cached =
        queryClient.getQueryData<InfiniteData<TranslateHistoryPage>>(
          infiniteQueryKey,
        );
      const firstPageItems = cached?.pages[0]?.data.list;
      if (firstPageItems) {
        queryClient.setQueryData(sidebarHistoryKeys.translate, firstPageItems);
      }
    };

    const runDetailPoll = async () => {
      if (!detailId) {
        return;
      }

      const { data } = await getTranslateInfo(detailId);
      patchTranslateDetailItem(queryClient, data);
      patchSidebarTranslationHistory(
        queryClient,
        data.file_translation_session_id,
        data,
      );
      if (infiniteQueryKey) {
        patchInfiniteTranslateHistoryItem(queryClient, infiniteQueryKey, data);
      }
    };

    const runListPoll = async () => {
      if (kind === 'sidebar') {
        const { data } = await getTranslateHistory({
          offset: 0,
          limit: pageSize,
        });
        queryClient.setQueryData(sidebarHistoryKeys.translate, data.list);
        return;
      }

      if (infiniteQueryKey) {
        await queryClient.refetchQueries({
          queryKey: infiniteQueryKey,
          exact: true,
        });
        syncSidebarFromInfiniteQuery();
        return;
      }

      if (!discoveryEnabled) {
        return;
      }

      const { data } = await getTranslateHistory({
        offset: 0,
        limit: pageSize,
        keyword: normalizedKeyword || undefined,
      });

      if (!normalizedKeyword) {
        queryClient.setQueryData(sidebarHistoryKeys.translate, data.list);
      }

      if (infiniteQueryKey) {
        replaceInfiniteTranslateHistoryFirstPage(
          queryClient,
          infiniteQueryKey,
          {
            data: {
              list: data.list,
              total: data.total,
            },
          },
        );
      }
    };

    const intervalMs =
      hasProcessingItems
        ? HIGH_FREQUENCY_POLL_MS
        : LOW_FREQUENCY_DISCOVERY_MS;

    const run = () => {
      if (kind === 'preview') {
        void runDetailPoll();
        return;
      }

      if (hasProcessingItems || discoveryEnabled) {
        void runListPoll();
      }
    };

    run();

    const timer = window.setInterval(run, intervalMs);
    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible') {
        run();
      }
    };
    const handleFocus = () => run();

    window.addEventListener('focus', handleFocus);
    document.addEventListener('visibilitychange', handleVisibilityChange);

    return () => {
      window.clearInterval(timer);
      window.removeEventListener('focus', handleFocus);
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, [
    discoveryEnabled,
    enabled,
    hasProcessingItems,
    infiniteQueryKey,
    isOwner,
    kind,
    detailId,
    normalizedKeyword,
    pageSize,
    processingIdsKey,
    queryClient,
  ]);

  return {
    isOwner,
    hasProcessingItems,
  };
}
